/**
 * Seguridad · Sesiones activas — muestra la sesión actual del usuario logueado
 * y, a partir del audit trail, las sesiones recientes (login/logout/timeout).
 *
 * Limitación arquitectural: como esta es una SPA cliente-side sin backend, no
 * podemos ver sesiones de OTROS usuarios — solo la propia. Para multi-usuario
 * en producción se requiere un endpoint server-side `/auth/sesiones-activas`
 * que liste tokens vivos por usuario.
 *
 * La sección "Historial de sesiones" sí muestra las acciones de todos los
 * usuarios (derivado del audit trail), útil para detectar patrones.
 */

import { useEffect, useMemo, useState } from 'react';
import { Activity, Clock, AlertTriangle, User, MapPin } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { fmtDateTime } from '@/lib/format';
import { useSessionStore } from '@/stores/session';
import { useUbicaciones } from '@/api/queries';
import { useAuditStore } from '@/stores/audit';
import {
  usePoliticaSesionStore,
  msHastaTimeout,
} from '@/stores/politica-sesion';

function formatDuracion(ms: number): string {
  if (ms < 0) return 'Expirada';
  const totalSeg = Math.floor(ms / 1000);
  const h = Math.floor(totalSeg / 3600);
  const m = Math.floor((totalSeg % 3600) / 60);
  const s = totalSeg % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

export function SesionesPage() {
  const usuario = useSessionStore((s) => s.usuario);
  const loginAt = useSessionStore((s) => s.loginAt);
  const ubicacionActivaId = useSessionStore((s) => s.ubicacionActivaId);
  const { data: ubicaciones = [] } = useUbicaciones();
  const ubicacion = ubicaciones.find((u) => u.id === ubicacionActivaId);

  const lastActivityAt = usePoliticaSesionStore((s) => s.lastActivityAt);
  const timeoutMinutos = usePoliticaSesionStore((s) => s.timeoutMinutos);
  const entries = useAuditStore((s) => s.entries);

  // Tick para refrescar el contador en vivo
  const [, forceTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => forceTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const tiempoRestante = msHastaTimeout(lastActivityAt, timeoutMinutos);
  const tiempoDesdeUltima = lastActivityAt
    ? Date.now() - new Date(lastActivityAt).getTime()
    : 0;
  const tiempoLogin = loginAt
    ? Date.now() - new Date(loginAt).getTime()
    : 0;

  // Historial: últimas 20 acciones sesión-related (login/logout/timeout)
  const historial = useMemo(() => {
    return entries
      .filter((e) =>
        ['login', 'logout', 'sesion.timeout', 'switch_location'].includes(e.accion),
      )
      .slice(-30)
      .reverse();
  }, [entries]);

  const cercaTimeout = tiempoRestante > 0 && tiempoRestante < 5 * 60_000; // <5min

  return (
    <div className="space-y-4">
      {/* Sesión actual */}
      <Card>
        <CardContent className="pt-4">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-everex-azul">
            <Activity className="h-4 w-4" /> Sesión actual
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <div>
              <div className="text-[11px] uppercase text-gray-600">Usuario</div>
              <div className="mt-1 flex items-center gap-2 text-sm font-bold">
                <User className="h-3 w-3 text-gray-600" />
                {usuario?.nombre ?? '—'}
              </div>
              <div className="font-mono text-[11px] text-gray-600">
                {usuario?.username ?? '—'} · {usuario?.rol ?? '—'}
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase text-gray-600">GLN activo</div>
              <div className="mt-1 flex items-center gap-2 text-sm font-bold">
                <MapPin className="h-3 w-3 text-gray-600" />
                {ubicacion?.nombre ?? '—'}
              </div>
              <div className="font-mono text-[11px] text-gray-600">
                GLN {ubicacion?.gln ?? '—'}
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase text-gray-600">Login</div>
              <div className="mt-1 text-sm font-bold">
                {loginAt ? fmtDateTime(loginAt) : '—'}
              </div>
              <div className="text-[11px] text-gray-600">
                Activa hace {formatDuracion(tiempoLogin)}
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase text-gray-600">Última actividad</div>
              <div className="mt-1 text-sm font-bold">
                {lastActivityAt ? fmtDateTime(lastActivityAt) : '—'}
              </div>
              <div className="text-[11px] text-gray-600">
                Hace {formatDuracion(tiempoDesdeUltima)}
              </div>
            </div>
          </div>

          <div className="mt-4 rounded border border-gray-100 bg-gray-50 p-3">
            <div className="flex items-center gap-2 text-xs">
              <Clock className={cn('h-3 w-3', cercaTimeout ? 'text-amber-600' : 'text-gray-600')} />
              <span className="text-gray-600">Auto-logout en:</span>
              <span
                className={cn(
                  'font-bold',
                  tiempoRestante < 0
                    ? 'text-red-600'
                    : cercaTimeout
                      ? 'text-amber-600'
                      : 'text-green-700',
                )}
              >
                {formatDuracion(tiempoRestante)}
              </span>
              <span className="text-[11px] text-gray-600">
                · timeout configurado: {timeoutMinutos} min
              </span>
            </div>
            {/* Barra de progreso */}
            <div className="mt-2 h-1.5 overflow-hidden rounded bg-gray-200">
              <div
                className={cn(
                  'h-full transition-all',
                  tiempoRestante < 0
                    ? 'bg-red-500'
                    : cercaTimeout
                      ? 'bg-amber-500'
                      : 'bg-green-500',
                )}
                style={{
                  width: `${Math.max(0, Math.min(100, (tiempoRestante / (timeoutMinutos * 60_000)) * 100))}%`,
                }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Limitación arquitectural */}
      <Alert className="border-amber-200 bg-amber-50">
        <AlertTriangle className="h-4 w-4 text-amber-700" />
        <AlertDescription className="text-xs text-amber-900">
          <b>Limitación del mockup:</b> esta es una SPA cliente — solo ves tu propia sesión.
          En producción, un endpoint <code className="font-mono">/auth/sesiones-activas</code>{' '}
          server-side permite listar las sesiones de todos los usuarios y forzar logout
          remoto. Documentado en el Handoff técnico.
        </AlertDescription>
      </Alert>

      {/* Historial de sesiones */}
      <Card>
        <CardContent className="pt-4">
          <div className="mb-3 text-sm font-semibold text-everex-azul">
            Historial de sesiones (audit trail)
          </div>
          {historial.length === 0 ? (
            <div className="py-8 text-center text-xs text-gray-600">
              Sin eventos de sesión registrados todavía.
            </div>
          ) : (
            <div className="overflow-x-auto rounded border border-gray-100">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Usuario</TableHead>
                    <TableHead>Acción</TableHead>
                    <TableHead>Razón</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {historial.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell className="font-mono text-[11px]">
                        {fmtDateTime(e.timestamp)}
                      </TableCell>
                      <TableCell className="text-xs">
                        <b>{e.usuarioNombreSnap}</b>
                      </TableCell>
                      <TableCell>
                        <span
                          className={cn(
                            'badge',
                            e.accion === 'login'
                              ? 'badge-green'
                              : e.accion === 'logout'
                                ? 'badge-gray'
                                : e.accion === 'sesion.timeout'
                                  ? 'badge-red'
                                  : 'badge-blue',
                          )}
                        >
                          {e.accion}
                        </span>
                      </TableCell>
                      <TableCell className="text-[11px] text-gray-600">
                        {e.razon ?? '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
